import { nanoid } from 'nanoid';
import React, {
	useMemo,
	useCallback,
	forwardRef,
	useImperativeHandle,
	useEffect,
	useRef,
} from 'react';
import { createEditor, Transforms, Editor, Text, Element, Descendant, BaseEditor } from 'slate';
import { Slate, Editable, withReact, useSlateStatic, ReactEditor, RenderElementProps } from 'slate-react';
import './CaptionEditor.scss';

type CustomText = { text: string };
type BadgeElement = {
  type: 'badge';
  label: string;
  value: string;
  id: string;
  children: CustomText[];
};
type ParagraphElement = { type: 'paragraph'; children: Descendant[] };
type CustomElement = BadgeElement | ParagraphElement;

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

interface VariableElementProps extends RenderElementProps {
  element: BadgeElement;
}

interface DynamicInlineEditorProps {
  value: Descendant[];
  onChange: (value: Descendant[]) => void;
  placeholder?: string;
  height?: 45 | 65 | 160;
  charLimit?: number;
}

export interface DynamicInlineEditorRef {
  insertVariable: (label: string, val: string) => void;
  insertEmoji: (emoji: string) => void;
  getValue: () => Descendant[];
  getPlainText: () => string;
  getTextLength: () => number;
}

const VariableElement: React.FC<VariableElementProps> = ({ attributes, children, element }) => {
	const editor = useSlateStatic();

	return (
		<span
			{...attributes}
			className="
        d-inline-flex align-items-center
        px-2 py-1
        bg-primary-subtle
        rounded-1
        mb-1
        user-select-none
        text-nowrap
        text-dark
      "
			contentEditable={false}
			data-slate-node="element"
		>
			<span className="me-1">{element.label}</span>

			<span
				className="ms-1 text-dark cursor-pointer fs-5 opacity-75"
				contentEditable={false}
				role="button"
				onMouseDown={(e) => {
					e.preventDefault();
					const path = ReactEditor.findPath(editor, element);
					Transforms.removeNodes(editor, { at: path });
				}}
			>
				×
			</span>
			{children}
		</span>
	);
};

// This ensures emojis are counted as 1.
// @ts-ignore - Intl.Segmenter is not yet in TypeScript lib but exists in modern browsers
const graphemeCounter = new Intl.Segmenter(undefined, {
	granularity: 'grapheme',
});

const countGraphemes = (text = ''): number =>
	[...graphemeCounter.segment(text)].length;

const sliceGraphemes = (text = '', max = 0): string => {
	if (max <= 0) return '';
	let out = '';
	let count = 0;

	for (const { segment } of graphemeCounter.segment(String(text))) {
		if (count >= max) break;
		out += segment;
		count += 1;
	}

	return out;
};

//To calculate editor content length
const getEditorLength = (editor: Editor): number => {
	let length = 0;

	for (const [node] of Editor.nodes(editor, { at: [] })) {
		// Text nodes
		if (Text.isText(node)) {
			length += countGraphemes(node.text);
		}

		// Badge nodes
		if (Element.isElement(node) && node.type === 'badge') {
			length += countGraphemes(node.label);
		}
	}

	return length;
};

const withInlines = (editor: Editor, charLimit?: number): Editor => {
	const { insertData, insertText, isInline, isElementReadOnly, isSelectable, isVoid } = editor;
	editor.isInline = (element) => (Element.isElement(element) && element.type === 'badge') || isInline(element);
	editor.isVoid = (element) => (Element.isElement(element) && element.type === 'badge') || isVoid(element);
	editor.isElementReadOnly = (element) => (Element.isElement(element) && element.type === 'badge') || isElementReadOnly(element);
	editor.isSelectable = (element) => !(Element.isElement(element) && element.type === 'badge') && isSelectable(element);
	editor.insertData = (data) => {
		const text = data.getData('text/plain');

		if (!charLimit) return insertData(data);

		const currentLength = getEditorLength(editor);
		const available = charLimit - currentLength;
		if (available <= 0) return;

		// Paste only allowed characters
		insertText(sliceGraphemes(text, available));
	};
	return editor;
};

const heightClassMap: Record<number, string> = {
	45: 'caption-editor-height-45',
	65: 'caption-editor-height-65',
	160: 'caption-editor-height-160',
};

const DynamicInlineEditor = forwardRef<DynamicInlineEditorRef, DynamicInlineEditorProps>(
	({ value, onChange, placeholder, height, charLimit }, ref) => {
		const editor = useMemo (() =>{
			const e = withReact(createEditor());
			return withInlines(e, charLimit);
		},[charLimit]);

		// slate-react maps nodes to their parents by object identity, so an editor
		// must own its nodes: if two editors mount with the same objects, the later
		// render overwrites the mapping and ReactEditor.findPath throws.
		// Mount-only, like Slate's own initialValue; remount via `key` to reset.
		const initialValue = useMemo(() => structuredClone(value), []); // eslint-disable-line react-hooks/exhaustive-deps

		const handleDOMBeforeInput = (event: InputEvent) => {
			const inputType = event.inputType;
			if (inputType === 'insertText' || inputType === 'insertFromPaste') {
				const textLength = getEditorLength(editor);
				if (charLimit && textLength >= charLimit) {
					event.preventDefault();
					return;
				}
			}
		};

		const renderElement = useCallback((props: RenderElementProps) => {
			if (Element.isElement(props.element) && props.element.type === 'badge')
				return <VariableElement {...props} element={props.element as BadgeElement} />;
			return <p className="text-black" {...props.attributes}>{props.children}</p>;
		}, []);


		const slateValueToPlainText = (value: Descendant[] = []): string => {
			let result = '';

			const traverse = (nodes: Descendant[]) => {
				for (const node of nodes) {
					// Text node
					if (Text.isText(node)) {
						result += node.text;
					}
					// Badge variable
					else if (Element.isElement(node) && node.type === 'badge') {
						result += node.value;
					}
					// Any other element
					else if ('children' in node) {
						traverse(node.children);
					}
				}
			};

			traverse(value);
			return result;
		};


		useImperativeHandle(ref, () => ({
			insertVariable(label: string, val: string) {
				const node: BadgeElement = {
					type: 'badge',
					label,
					id: nanoid(),
					value: val,
					children: [{ text: '' }],
				};
				const textLength = getEditorLength(editor);
				if (charLimit && textLength + label.length > charLimit) {
					return; // Do not insert if it exceeds char limit
				}
				Transforms.insertNodes(editor, node);
				// When inserting a void inline node, Slate may leave the node selected.
				// Move the selection to a point AFTER the badge so the caret is visible.
				try {
					const after = (editor.selection?.focus
						? Editor.after(editor, editor.selection.focus, { unit: 'offset' })
						: null) || Editor.end(editor, []);
					Transforms.select(editor, after);
				} catch (e) {
					// ignore
				}
				Transforms.insertText(editor, ' ');
				ReactEditor.focus(editor);
			},

			insertEmoji(emoji: string) {
				const textLength = getEditorLength(editor);
				if (charLimit && textLength + 1 > charLimit) {
					return; // Do not insert if it exceeds char limit
				}
				Transforms.insertText(editor, emoji);
				ReactEditor.focus(editor);
			},

			getValue() {
				return editor.children;
			},

			getPlainText() {
				return slateValueToPlainText(editor.children);
			},

			getTextLength() {
				return getEditorLength(editor);
			},
		}));

		//To update the count value on first load
		const initialChangeRef = useRef(false);
		useEffect(() => {
			if (initialChangeRef.current) return;
			initialChangeRef.current = true;
			if (Array.isArray(value)) {
				onChange([...value]);
			}
		}, []);

		return (
			<Slate
				editor={editor}
				initialValue={initialValue}
				onChange={(newVal) => onChange([...newVal])}
			>
				<Editable
					spellCheck
					className={`pt-2 ps-2 rounded-1 border overflow-x-hidden editor-inline mt-1 caption-editor-font ${height ? heightClassMap[height] || '' : ''}`}
					placeholder={placeholder}
					renderElement={renderElement}
					onDOMBeforeInput={handleDOMBeforeInput as any}
				/>
			</Slate>
		);
	},
);

DynamicInlineEditor.displayName = 'DynamicInlineEditor';

export default DynamicInlineEditor;
